import styled, { css } from 'styled-components';

import { Button, Text } from '../../../../components';
import { COLORS, ANIM, REDUCED_MOTION, MOBILE, SPACE } from '@/nova/theme/tokens';
import { hoverWash } from '@/nova/theme/mixins';

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  /* Roomy vertical rhythm: doubled breathing room below the tabs (top) and above the cards. */
  margin-top: 16px;
  gap: 36px;
`;

export const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
`;

/* Two-line intro: what the tab is (line 1) + a reorder hint (line 2) prefixed with a grab glyph. */
export const LeadWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  max-width: 72ch;
`;

export const Lead = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${COLORS.textMuted};
`;

/* The reorder hint on its own line, led by a grab/move glyph (or an up/down glyph on mobile, where
   reordering is by buttons) so the drag affordance is unmistakable. */
export const LeadHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};

  svg {
    flex: 0 0 auto;
    color: ${COLORS.ctaBorder};
  }
`;

/* The header's right-side actions (Add + Publish), with the unsaved-changes cue. */
export const Actions = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  ${MOBILE} {
    /* Fill the row and ALLOW shrink so the built-in flex-wrap actually engages. As flex:0 0 auto the
       cluster stays at single-line max-content (status + Preview + Publish + kebab) and runs off the
       right — cutting off Publish and making the whole page scroll horizontally. Bounding its width
       lets the controls wrap onto the phone instead. */
    flex: 1 1 100%;
    min-width: 0;
    justify-content: flex-start;
  }
`;

/* The two primary actions (Preview + Publish). Inline pills on desktop; on a phone they drop below the
   meta row (status + kebab) and split it as two equal, full-width halves — the familiar mobile
   action-pair, so the buttons line up instead of wrapping at mismatched widths. */
export const Buttons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;

  ${MOBILE} {
    order: 3;
    flex: 1 1 100%;
    gap: 10px;

    & > button {
      flex: 1 1 0;
      min-width: 0;
      justify-content: center;
    }
  }
`;

/* Wraps the overflow menu so it can be ordered onto the meta row (right of the status) on a phone
   rather than trailing the buttons. */
export const KebabWrap = styled.div`
  display: inline-flex;
  align-items: center;

  ${MOBILE} {
    order: 2;
    margin-left: auto;
  }
`;

/* "Add banners" — the orange outline CTA that opens the pool picker. */
export const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 999px;
  border: 1px solid ${COLORS.ctaBorder};
  background: transparent;
  color: ${COLORS.ctaBorder};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE},
    color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.ctaTint};
  }
`;

/* Preview on site, on a banner carousel and on a game list (ListActions), in the Games tab's Add list look (the shared
   outline Button: its dark surface, orange ring and light copy) at the size of the actions beside it: AddBtn's padding
   (9px, off the scale), so it stands as tall as they do, with their 13px copy and 14px glyph. Icon-only on a game
   list's short row, it stays the pill they are, not Button's 40px circle (48px on a phone). */
export const PreviewBtn = styled(Button)`
  width: auto;
  height: auto;
  padding: 9px ${SPACE[16]};
  gap: ${SPACE[8]};
  font-size: 13px;
  font-weight: 700;

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* "Publish to site" — commits the current curation + order. Primary publish-blue; disabled with no
   unsaved changes. */
export const PublishBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px solid ${COLORS.publishBlue};
  background: ${COLORS.publishBlue};
  color: ${COLORS.ctaSolidText};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: filter ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/* The "unsaved changes" cue — a soft dot + label, shown while the draft differs from what's published. */
export const DirtyNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: ${COLORS.textMuted};

  /* The status leads the meta row on a phone (buttons drop to their own row below). */
  ${MOBILE} {
    order: 1;
  }

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${COLORS.ctaSolid};
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  /* Equal-height rows so the trailing "add" tile matches a full card even on a row of its own. */
  grid-auto-rows: 1fr;
  gap: 16px;
`;

/* One curated banner in the ordered list — a draggable slot wrapping the card, with an order badge and
   a remove control. The list reorders in REAL TIME as you drag (see CarouselCuration.tsx): the grabbed
   card becomes a dimmed placeholder (the drag ghost carries the visual) that tracks the cursor, and the
   other cards FLIP-glide out of its way. */
export const Slot = styled.div<{ $dragging?: boolean }>`
  position: relative;
  border-radius: 16px;
  cursor: grab;
  will-change: transform;
  user-select: none; /* so a drag doesn't start a text/image selection instead */
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:active {
    cursor: grabbing;
  }

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.35;
    `}

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* Custom drag element (see CarouselCuration.tsx): a real node that FOLLOWS the pointer while the native
   drag ghost is suppressed, so the lifted card is fully solid + shadowed with no snapshot clipping or
   transparency. Portalled to <body>; moved via an imperative transform on this wrapper. */
export const DragLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  pointer-events: none;
  will-change: transform;
`;

/* The lifted card inside the drag layer: the same solid card, raised — a soft drop shadow plus a light
   top rim (so it still reads as raised over the near-black page) and a slight scale so it clearly looks
   picked up. */
export const DragGhost = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  box-shadow:
    ${COLORS.menuShadow},
    inset 0 1px 0 ${COLORS.hoverBorderLit};
  transform: scale(1.03);
  transform-origin: center;
`;

/* A curated banner reads as just its artwork + title — no edit / origin / version chrome (that lives in
   the pool). The curation controls (order badge + remove) overlay it from the Slot. */
export const SlotCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  /* Solid (opaque) surface, not the translucent surfaceFaint — a translucent footer looked hollow
     in the drag snapshot. Now the whole card reads solid while dragged. */
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const CardArt = styled.div`
  aspect-ratio: 2 / 1;
  background: ${COLORS.surfaceInset};
`;

export const CardImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none; /* so grabbing the image drags the CARD, not the image file */
  user-select: none;
`;

/* Title row: a grip affordance + the banner title. The whole card is the drag source; the grip is just
   an affordance (it does not have to be grabbed). */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 11px;
`;

export const DragHandle = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  color: ${COLORS.textFaint};
`;

export const CardTitle = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 650;
  color: ${COLORS.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Lower-right edit control — jumps straight to the pool editor for THIS banner, so a curated card
   can be retouched from anywhere (not only from the pool tab). Solid fill + hairline so it reads as
   a button; grows to a 48×48 tap target on mobile. */
export const EditBtn = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  cursor: pointer;
  transition: filter ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover {
    filter: brightness(1.35);
  }

  ${MOBILE} {
    width: 48px;
    height: 48px;
  }
`;

/* Mobile reorder controls — replace the drag handle with up/down buttons so the list can be
   touch-scrolled (dragging is disabled on mobile). Each is a full 48×48 tap target. */
export const MoveBtns = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  gap: 6px;
`;

export const MoveBtn = styled.button<{ $down?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  cursor: pointer;
  transition: filter ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  svg {
    transform: ${({ $down }) => ($down ? 'rotate(180deg)' : 'none')};
  }

  &:hover:not(:disabled) {
    filter: brightness(1.35);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* The position number (1-based) — the carousel order the customer site will render. Solid surface (not a
   translucent scrim) so it reads cleanly over any artwork; a hairline defines its edge. */
export const OrderBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const SlotBtns = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  gap: 6px;
`;

export const SlotBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.heading};
  background: ${COLORS.surfaceCard};
  cursor: pointer;
  transition: filter ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover {
    filter: brightness(1.35);
  }
`;

/* Empty carousel: a dashed panel prompting the owner to add banners from the pool. */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 44px 24px;
  border: 1px dashed ${COLORS.hairline};
  border-radius: 16px;
  background: ${COLORS.surfaceFaint};
  text-align: center;
`;

export const EmptyTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const EmptyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* The "add another" tile that closes the ordered grid — a dashed placeholder with a + so it's
   obvious how to grow the carousel (the header's "Add banners" button does the same). Opens the
   pool picker. Stretches to match the card height when it shares a row; falls back to a 2:1 tile
   when it wraps onto its own row. */
export const AddTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  /* No fixed aspect: it stretches to a full card's height via the grid's equal-height rows. */
  min-height: 150px;
  border-radius: 16px;
  border: 1.5px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textMuted};
  cursor: pointer;
  /* The shared highlight-on-hover (faint wash + proximity-lit ring), same as every clickable card —
     not an orange accent. */
  ${hoverWash}
`;

/* The empty-state variant of the add tile: width-constrained so it reads as a centred CTA under the
   "no banners yet" copy, rather than stretching the full row. */
export const EmptyAddTile = styled(AddTile)`
  width: min(320px, 100%);
  margin-top: 12px;
`;

export const AddTilePlus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1.5px dashed currentColor;
`;

export const AddTileLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 650;
`;

/* The pool picker (the "Add banners" Sheet body) now lives in the shared page-local `PoolPickerSheet`
   component (reused by the storyboard composer's promotion picker). */
